import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import AuthPage from './components/Auth/AuthPage';
import ResetPasswordPage from './components/Auth/ResetPasswordPage';
import AccountDeletionPending from './components/AccountDeletionPending';
import PrivacyPolicy from './components/PrivacyPolicy';
import Splash, { useSplashGate } from './components/Splash';
import { fetchDeletionState } from './lib/accountService';
import { usePrivacyRoute } from './lib/privacyRoute';
import { captureAttribution } from './lib/attribution';
import { useAuth } from './hooks/useAuth';
import reportWebVitals from './reportWebVitals';
import { startVersionCheck } from './utils/versionCheck';
import { ACADEMY_ENABLED, INVESTOR_PROFILE_ENABLED, CALCULATORS_ENABLED } from './constants/features';

// Account-deletion gate. An account inside its grace window gets the pending
// screen INSTEAD of the app, not layered over it: App's init effect starts
// loading the portfolio, quotes and sector lookups the moment it mounts, so a
// screen drawn on top would still be pulling that user's data underneath.
//
// Three states, and the middle one matters: `undefined` means the answer has
// not come back yet and nothing is rendered, exactly as App.js:119 does for the
// questionnaire gate. Rendering the app first and swapping it out would flash
// the whole product at somebody who no longer has access to it.
//
// Note for anyone extending this: the gate blocks the UI, not the API. A token
// held outside the app can still write to that user's own rows during the
// grace window, because RLS says nothing about deletion_requested_at. Closing
// that means adding `deletion_requested_at is null` to the policies on every
// user table — a schema change across nine tables, deliberately not made here.
function SessionGate({ session, onSignOut }) {
  const userId = session?.user?.id;
  const isGuest = !!session?.user?.is_anonymous;

  // undefined = still checking · null = active account · string = requested at
  const [deletionAt, setDeletionAt] = useState(isGuest ? null : undefined);
  // The splash owns its own exit: once the mark is up it stays for its
  // minimum and fades, instead of vanishing the moment the check returns.
  const gate = useSplashGate(deletionAt === undefined);

  useEffect(() => {
    // A guest can never carry the mark: account-delete-request refuses
    // anonymous callers, so there is nothing to look up and no reason to make
    // guest startup wait on a round trip.
    if (isGuest) { setDeletionAt(null); return undefined; }
    let alive = true;
    setDeletionAt(undefined);
    (async () => {
      const at = await fetchDeletionState(userId);
      if (alive) setDeletionAt(at);
    })();
    return () => { alive = false; };
  }, [userId, isGuest]);

  if (gate !== 'off') return <Splash leaving={gate === 'leaving'}/>;
  if (deletionAt) {
    return (
      <AccountDeletionPending
        requestedAt={deletionAt}
        onRestored={() => setDeletionAt(null)}
      />
    );
  }
  return <App onSignOut={onSignOut} session={session} />;
}

function Root() {
  const {
    session, loading, degraded, recovery,
    signUp, signIn, signInAnonymously, signOut,
    resetPassword, updatePassword, endRecovery,
  } = useAuth();

  const privacy = usePrivacyRoute();
  const authGate = useSplashGate(loading);

  // Checked before `loading`, and before the session: the policy has to be
  // readable by somebody who has not signed up, which is the whole reason it
  // has a URL. Waiting on the auth round trip would put a splash screen in
  // front of a legal document that needs no account to read.
  if (privacy) return <PrivacyPolicy/>;

  if (authGate !== 'off') return <Splash leaving={authGate === 'leaving'}/>;

  // Checked before the session, and deliberately so: a recovery link produces a
  // valid session, so testing `session` first would send the user straight into
  // the app and silently skip the password form the link exists for.
  if (recovery) return <ResetPasswordPage onUpdatePassword={updatePassword} onDone={endRecovery} />;

  if (!session) return <AuthPage onSignIn={signIn} onSignUp={signUp} onGuest={signInAnonymously} onForgotPassword={resetPassword} degraded={degraded} />;

  return <SessionGate session={session} onSignOut={signOut} />;
}

// /academy leads to the home screen while the Academy is hidden
// (constants/features.js). Netlify answers it with a 302 first (netlify.toml);
// this covers any other host, and keeps the query string (attribution).
// The same for the investor profile and the calculators while they are hidden.
const HIDDEN_PATHS = [
  ...(ACADEMY_ENABLED ? [] : ['academy']),
  ...(INVESTOR_PROFILE_ENABLED ? [] : ['profile']),
  ...(CALCULATORS_ENABLED ? [] : ['calculators']),
];
if (HIDDEN_PATHS.length && new RegExp(`^/(${HIDDEN_PATHS.join('|')})(/|$)`, 'i').test(window.location.pathname)) {
  window.history.replaceState(null, '', '/' + window.location.search + window.location.hash);
}

// utm_* off the first URL this browser opens, before anything renders and
// before React can rewrite the address bar (lib/attribution.js).
captureAttribution();

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<Root />);

// Register the service worker for installability + push. Guarded so a failure
// (or unsupported browser) never affects the app.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => { /* non-fatal */ });
  });
}

// Resume after a deploy → reload onto the new build (utils/versionCheck.js).
startVersionCheck();

reportWebVitals();
